const apiBaseUrl = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/u, '');

if (!apiBaseUrl) {
  console.error('VITE_API_URL is missing.');
}

type ApiRequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
};

type ApiErrorBody = {
  error?: string;
  message?: string;
  creditsRequired?: number;
  creditsRemaining?: number;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly creditsRequired: number | null;
  readonly creditsRemaining: number | null;

  constructor(
    status: number,
    code: string,
    message: string,
    creditsRequired: number | null = null,
    creditsRemaining: number | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.creditsRequired = creditsRequired;
    this.creditsRemaining = creditsRemaining;
  }
}

export function buildApiUrl(path: string) {
  if (!apiBaseUrl) {
    throw new Error('VITE_API_URL is missing.');
  }

  return `${apiBaseUrl}/${path.replace(/^\/+/, '')}`;
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  let body: BodyInit | undefined;

  if (options.body instanceof FormData || options.body instanceof Blob) {
    body = options.body;
  } else if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(options.body);
  }

  const response = await fetch(buildApiUrl(path), {
    ...options,
    headers,
    body,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({})) as ApiErrorBody;
    throw new ApiError(
      response.status,
      errorBody.error ?? 'api_error',
      errorBody.message ?? `API request failed (${response.status}).`,
      typeof errorBody.creditsRequired === 'number' ? errorBody.creditsRequired : null,
      typeof errorBody.creditsRemaining === 'number' ? errorBody.creditsRemaining : null,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}
